/**
 * The requirement heat maps: TBRs and verification coverage (issue #94).
 *
 * One component for both, because they are one table - a segment or subsystem
 * down the side, the level across the top - read two ways. The counting is in
 * `heatmap.ts`; this file only draws it.
 *
 * A TABLE, not an SVG: every cell is a number somebody will want to read, copy
 * or have read out, and a table is already the accessible form of a heat map.
 * The colour is a single hue light to dark, and every cell states its number,
 * so nothing is carried by colour alone.
 */

import { useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { api } from '../../api'
import type { FlowdownRow } from '../../schema'
import { MONOSPACE } from '../../theme'
import EmptyState from '../common/EmptyState'
import { axisLabel, crossTab, intensity } from './heatmap'
import type { HeatAxis, HeatCell, HeatMeasure, HeatTable } from './heatmap'
import { useDiagramRows } from './useDiagramRows'

/** The lightest and darkest fill. Capped so the cell's number stays readable on it. */
const FLOOR = 0.08
const CEILING = 0.62

export default function RequirementHeatMap({ measure }: { measure: HeatMeasure }) {
  const { rows, error } = useDiagramRows<FlowdownRow>(api.flowdown)
  const theme = useTheme()
  const [side, setSide] = useState<HeatAxis>('subsystem')
  const table = useMemo(() => (rows ? crossTab(rows, side, 'level') : null), [rows, side])

  // TBRs keep the colour the hierarchy already gives them; coverage is a plain
  // magnitude, so it takes the primary hue rather than a status colour.
  const hue = measure === 'unresolved' ? theme.palette.warning.main : theme.palette.primary.main
  const fill = (cell: HeatCell | undefined) => {
    const heat = table ? intensity(cell, measure, table) : null
    return heat === null || heat === 0 ? 'transparent' : alpha(hue, FLOOR + (CEILING - FLOOR) * heat)
  }

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!rows || !table) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }
  if (table.total.total === 0) {
    return (
      <EmptyState
        title="There are no requirements to count"
        description="This map is generated from GET /api/flowdown. Import a model, or create a requirement, and it appears here."
      />
    )
  }

  const word = side === 'subsystem' ? 'subsystem' : 'segment'
  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {measure === 'unresolved'
          ? `Requirements whose status is TBD, TBR or TBC, by ${word} and level. The darker the ` +
            'cell, the more unresolved requirements are in it, against the busiest cell — so the ' +
            'next sweep can be aimed where it clears the most.'
          : `The share of requirements a verification case verifies, by ${word} and level. The ` +
            'darker the cell, the less of it is verified; a cell whose requirements are all ' +
            'verified is left clear.'}{' '}
        Generated from <code>GET /api/flowdown</code>; each requirement is counted once.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Rows by
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={side}
          aria-label="Rows by"
          onChange={(_event, next: HeatAxis | null) => next && setSide(next)}
        >
          <ToggleButton value="subsystem" sx={{ py: 0.25 }}>
            Subsystem
          </ToggleButton>
          <ToggleButton value="segment" sx={{ py: 0.25 }}>
            Segment
          </ToggleButton>
        </ToggleButtonGroup>
        <Scale hue={hue} measure={measure} table={table} />
      </Stack>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" aria-label={measure === 'unresolved' ? 'TBR heat map' : 'Coverage heat map'}>
          <TableHead>
            <TableRow>
              <TableCell>{side === 'subsystem' ? 'Subsystem' : 'Segment'}</TableCell>
              {table.columns.map((column) => (
                <TableCell key={column} align="center" sx={{ fontFamily: MONOSPACE }}>
                  {axisLabel('level', column)}
                </TableCell>
              ))}
              <TableCell align="center">All levels</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row) => (
              <TableRow key={row}>
                <TableCell component="th" scope="row" sx={{ fontFamily: MONOSPACE }}>
                  {axisLabel(side, row)}
                </TableCell>
                {table.columns.map((column) => (
                  <Cell
                    key={column}
                    cell={table.cells[row]?.[column]}
                    measure={measure}
                    where={`${axisLabel(side, row)}, ${axisLabel('level', column)}`}
                    background={fill(table.cells[row]?.[column])}
                  />
                ))}
                <Cell
                  cell={table.rowTotals[row]}
                  measure={measure}
                  where={`${axisLabel(side, row)}, all levels`}
                  background="transparent"
                  strong
                />
              </TableRow>
            ))}
            <TableRow>
              <TableCell component="th" scope="row">
                All {word}s
              </TableCell>
              {table.columns.map((column) => (
                <Cell
                  key={column}
                  cell={table.columnTotals[column]}
                  measure={measure}
                  where={`${axisLabel('level', column)}, every ${word}`}
                  background="transparent"
                  strong
                />
              ))}
              <Cell cell={table.total} measure={measure} where="the whole program" background="transparent" strong />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

/** One count, with the sentence behind it on hover. */
function Cell({
  cell,
  measure,
  where,
  background,
  strong = false,
}: {
  cell: HeatCell | undefined
  measure: HeatMeasure
  where: string
  background: string
  strong?: boolean
}) {
  if (!cell || cell.total === 0) {
    return (
      <TableCell align="center" sx={{ color: 'text.disabled' }} aria-label={`${where}: no requirements`}>
        ·
      </TableCell>
    )
  }
  const unverified = cell.total - cell.verified
  const [main, sub, sentence] =
    measure === 'unresolved'
      ? [
          String(cell.unresolved),
          `of ${cell.total}`,
          `${where}: ${cell.unresolved} of ${cell.total} requirement${cell.total === 1 ? '' : 's'} TBD, TBR or TBC`,
        ]
      : [
          // The VERIFIED share, though the colour measures the gap: a bare
          // "100%" in a coverage table is read as full coverage whatever the
          // heading says, so the number says what is covered.
          `${Math.round((100 * cell.verified) / cell.total)}%`,
          `${cell.verified} of ${cell.total} verified`,
          `${where}: ${unverified} of ${cell.total} requirement${cell.total === 1 ? '' : 's'} not verified ` +
            `by any verification case; ${cell.verified} verified`,
        ]
  return (
    <Tooltip title={sentence}>
      <TableCell align="center" sx={{ bgcolor: background, whiteSpace: 'nowrap' }} aria-label={sentence}>
        <Typography variant="body2" component="span" sx={{ fontWeight: strong ? 600 : 400 }}>
          {main}
        </Typography>{' '}
        <Typography variant="caption" component="span" color="text.secondary">
          {sub}
        </Typography>
      </TableCell>
    </Tooltip>
  )
}

/** What the shades mean: five steps of the one hue, with their end points named. */
function Scale({ hue, measure, table }: { hue: string; measure: HeatMeasure; table: HeatTable }) {
  const steps = [0.25, 0.5, 0.75, 1]
  const [low, high] =
    measure === 'unresolved' ? ['none', `${table.maxUnresolved} in a cell`] : ['all verified', 'none verified']
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', ml: { sm: 2 } }} aria-hidden="true">
      <Typography variant="caption" color="text.secondary">
        {low}
      </Typography>
      <Box sx={{ width: 14, height: 14, border: 1, borderColor: 'divider', borderRadius: 0.5 }} />
      {steps.map((step) => (
        <Box
          key={step}
          sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: alpha(hue, FLOOR + (CEILING - FLOOR) * step) }}
        />
      ))}
      <Typography variant="caption" color="text.secondary">
        {high}
      </Typography>
    </Stack>
  )
}
